import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";
import { API_BASE_URL } from "./api";
import { Button, Field, SectionHeader, SurfaceCard } from "./components/designSystem";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const queryEmail = searchParams.get("email");
    const queryToken = searchParams.get("token");

    if (queryEmail) {
      setEmail(queryEmail);
    }
    if (queryToken) {
      setToken(queryToken);
    }
  }, [searchParams]);

  const passwordStrength = useMemo(() => {
    if (!password) return "Not entered";
    if (password.length < 6) return "Too short";
    if (!/[A-Z]/.test(password) || !/[0-9]/.test(password)) return "Weak";
    if (password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) return "Strong";
    return "Medium";
  }, [password]);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, password })
      });
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setResult({ error: "Network error" });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[calc(100vh-140px)] bg-[#f3efe7] px-6 py-12 dark:bg-[linear-gradient(180deg,_#020617_0%,_#111827_45%,_#0f172a_100%)] lg:px-10">
      <div className="mx-auto grid max-w-5xl gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <SurfaceCard className="overflow-hidden bg-[linear-gradient(135deg,_#0f172a,_#111827_58%,_#1d4ed8)] text-white dark:border-white/10">
          <SectionHeader
            eyebrow="Reset password"
            title="Set a new account password"
            description="Use the email, token, and new password together. If the reset link included query parameters, they are prefilled automatically."
          />
          <div className="mt-8 rounded-3xl border border-white/10 bg-white/10 p-4 text-sm leading-7 text-slate-200">
            This flow currently surfaces backend validation details directly, which is useful while the authentication experience is still being hardened.
          </div>
        </SurfaceCard>

        <SurfaceCard className="bg-white dark:bg-gray-900">
          <SectionHeader
            eyebrow="Credential update"
            title="Enter reset details"
            description="The token must match the one generated for the email address, and the token must still be valid."
          />
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <Field label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
            <Field label="Token" type="text" value={token} onChange={e => setToken(e.target.value)} placeholder="Paste reset token" required />
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">New Password</span>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-11 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100 dark:focus:border-gray-500"
                  placeholder="New password"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-3 text-slate-500 dark:text-slate-400"
                  tabIndex={-1}
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </label>
            <p className="text-xs text-slate-500 dark:text-slate-400">Password strength: <span className="font-semibold">{passwordStrength}</span></p>
            <Button type="submit" variant="brand" disabled={loading}>
              <KeyRound className="h-4 w-4" />
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
          {result ? (
            <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-5 text-sm dark:border-gray-700 dark:bg-gray-800">
              {result.success ? (
                <>
                  <p className="font-semibold text-emerald-600">{result.message}</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Button type="button" variant="brand" onClick={() => navigate("/login")}>Go to Login</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate("/forgot-password")}>Request New Token</Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-semibold text-rose-600">{result.error || result.message}</p>
                  {result.details && Array.isArray(result.details) ? (
                    <ul className="mt-3 list-disc pl-5 text-sm text-rose-500">
                      {result.details.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
          <div className="mt-5 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <ShieldCheck className="h-4 w-4" />
            Need a new token? <Link to="/forgot-password" className="font-semibold text-sky-600">Return to forgot password</Link>
          </div>
        </SurfaceCard>
      </div>
    </div>
  );
}
